/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { type FunctionComponent, useCallback, useState } from 'react';

import { jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

import { UI_EVENT_TYPE } from '@atlaskit/analytics-gas-types';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import Button from '@atlaskit/button/default/button';
import ModalBody from '@atlaskit/modal-dialog/modal-body';
import AkModalDialog from '@atlaskit/modal-dialog/modal-dialog';
import AkModalTransition from '@atlaskit/modal-dialog/modal-transition';

import {
	ANALYTICS_CHANNEL,
	DEFAULT_TEST_ID,
	MODAL_DIALOG_TEST_ID,
	TRIGGER_ELEMENT_TEST_ID,
} from '../../constants';
import { AnalyticsContextWrapper } from '../../services/analytics-wrapper';
import type { GlobalInviteProps, ModalDialogContentProps } from '../../types';
import { messages } from './messages';

// eslint-disable-next-line @typescript-eslint/no-restricted-types
export function withModalDialog(Content: FunctionComponent<ModalDialogContentProps>) {
	return function ModalDialog({
		testId = DEFAULT_TEST_ID,
		triggerElement: TriggerElement,
		children,
		...props
	}: GlobalInviteProps): JSX.Element {
		const { createAnalyticsEvent } = useAnalyticsEvents();
		const { formatMessage } = useIntl();

		const [isModalDialogOpen, setIsModalDialogOpen] = useState<boolean>(false);
		const [adminHasNavigatedAway, setAdminHasNavigatedAway] = useState<boolean>(false);

		const openModalDialog = useCallback(() => {
			createAnalyticsEvent({
				eventType: UI_EVENT_TYPE,
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'globalInviteButton',
				attributes: {
					componentName: 'globalInvite',
					product: props.product,
				},
			}).fire(ANALYTICS_CHANNEL);

			setIsModalDialogOpen(true);
		}, [createAnalyticsEvent, props.product]);

		const onAdminMenuItemClick = useCallback((event?: React.MouseEvent | React.KeyboardEvent) => {
			// See packages/people-and-teams/invite-people/src/components/DropdownMenu/AdminMenuGroup.tsx for where these items are created.
			// Handle the click event happening on a link directly, or on an inner <span> or similar
			const isLink = (event?.target as HTMLElement)?.tagName === 'A';
			const link = isLink
				? (event?.target as HTMLElement)
				: (event?.target as HTMLElement)?.closest('a');

			if (
				link &&
				(link?.getAttribute('target') === 'new' || link?.getAttribute('target') === '_blank')
			) {
				setAdminHasNavigatedAway(true);
			}
		}, []);

		const onCancel = useCallback(() => {
			createAnalyticsEvent({
				eventType: UI_EVENT_TYPE,
				action: adminHasNavigatedAway ? 'closed' : 'cancelled',
				actionSubject: 'modalDialog',
				actionSubjectId: 'globalInviteModal',
				attributes: {
					componentName: 'globalInvite',
					product: props.product,
				},
			}).fire(ANALYTICS_CHANNEL);

			setIsModalDialogOpen(false);
		}, [createAnalyticsEvent, props.product, adminHasNavigatedAway]);

		const onInviteCompleted = useCallback(() => {
			createAnalyticsEvent({
				eventType: UI_EVENT_TYPE,
				action: 'completed',
				actionSubject: 'modalDialog',
				actionSubjectId: 'globalInviteModal',
				attributes: {
					componentName: 'globalInvite',
					product: props.product,
				},
			}).fire(ANALYTICS_CHANNEL);

			setIsModalDialogOpen(false);
		}, [createAnalyticsEvent, props.product]);

		const focusDialog = useCallback((node: HTMLElement) => {
			// Focus the dialog's container when it finishes opening
			node.focus();
		}, []);

		return (
			<AnalyticsContextWrapper product={props.product}>
				<div data-testid={testId}>
					{TriggerElement ? (
						<TriggerElement onClick={openModalDialog} />
					) : (
						<Button
							shouldFitContainer
							onClick={openModalDialog}
							testId={`${testId}--${TRIGGER_ELEMENT_TEST_ID}`}
						>
							{children}
						</Button>
					)}

					<AkModalTransition>
						{isModalDialogOpen ? (
							// eslint-disable-next-line @atlaskit/design-system/no-modal-label
							<AkModalDialog
								width="small"
								shouldScrollInViewport
								shouldReturnFocus
								onClose={onCancel}
								onOpenComplete={focusDialog}
								label={formatMessage(messages.modalDialogLabel)}
								testId={`${testId}--${MODAL_DIALOG_TEST_ID}`}
							>
								<ModalBody>
									<Content
										onAdminMenuItemClick={onAdminMenuItemClick}
										onCancel={onCancel}
										onInviteCompleted={onInviteCompleted}
										{...props}
									/>
								</ModalBody>
							</AkModalDialog>
						) : null}
					</AkModalTransition>
				</div>
			</AnalyticsContextWrapper>
		);
	};
}
